import * as React from "react";
import { AlertCircle } from "lucide-react";
import { cn } from "lib/utils";

/* ══════════════════════════════════════════════════════════════════════════
   Form field scaffolding.

   Raw `<label>` + `<input>` pairs across the app usually skipped the `htmlFor`
   link, had no `aria-describedby` for hints, and showed errors as plain red
   text with no `role="alert"`. `Field` wires all of that automatically: it
   generates an id, links label → control, and marks the control invalid when
   an error is present.
   ══════════════════════════════════════════════════════════════════════════ */

export interface FieldProps {
    label?: React.ReactNode;
    /** Helper text shown under the control (hidden once an error appears) */
    hint?: React.ReactNode;
    error?: React.ReactNode;
    required?: boolean;
    /** Small right-aligned note next to the label, e.g. "Optional", "0/120" */
    aside?: React.ReactNode;
    /** Render prop receives the ids/aria to spread onto the control */
    children: React.ReactNode | ((props: FieldControlProps) => React.ReactNode);
    className?: string;
    /** Span both columns inside a <FormGrid> */
    full?: boolean;
    id?: string;
}

export interface FieldControlProps {
    id: string;
    "aria-describedby"?: string;
    "aria-invalid"?: boolean;
    "aria-required"?: boolean;
}

export const Field: React.FC<FieldProps> = ({
    label,
    hint,
    error,
    required,
    aside,
    children,
    className,
    full,
    id: idProp,
}) => {
    const generated = React.useId();
    const id = idProp ?? `field-${generated}`;
    const hintId = `${id}-hint`;
    const errorId = `${id}-error`;

    const describedBy = [error ? errorId : null, hint && !error ? hintId : null]
        .filter(Boolean)
        .join(" ") || undefined;

    const controlProps: FieldControlProps = {
        id,
        "aria-describedby": describedBy,
        "aria-invalid": error ? true : undefined,
        "aria-required": required || undefined,
    };

    return (
        <div className={cn("min-w-0", full && "sm:col-span-2", className)}>
            {label && (
                <div className="flex items-baseline justify-between gap-2 mb-1.5">
                    <label htmlFor={id} className="text-meta font-semibold text-content-secondary">
                        {label}
                        {required && (
                            <span className="text-danger ml-0.5" aria-hidden="true">
                                *
                            </span>
                        )}
                    </label>
                    {aside && <span className="text-micro text-content-subtle flex-shrink-0">{aside}</span>}
                </div>
            )}

            {typeof children === "function" ? children(controlProps) : children}

            {error ? (
                <p id={errorId} role="alert" className="mt-1.5 flex items-start gap-1 text-micro font-medium text-danger">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-px" aria-hidden="true" />
                    <span>{error}</span>
                </p>
            ) : hint ? (
                <p id={hintId} className="mt-1.5 text-micro text-content-muted leading-relaxed">
                    {hint}
                </p>
            ) : null}
        </div>
    );
};

/** Two-column form grid that collapses to one column on phones. */
export const FormGrid: React.FC<React.HTMLAttributes<HTMLDivElement> & { columns?: 1 | 2 }> = ({
    columns = 2,
    className,
    children,
    ...props
}) => (
    <div
        className={cn(
            "grid gap-4",
            columns === 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1",
            className
        )}
        {...props}
    >
        {children}
    </div>
);

/** Titled group of fields — gives long forms a scannable structure. */
export const FormSection: React.FC<
    Omit<React.HTMLAttributes<HTMLElement>, "title"> & { title?: React.ReactNode; description?: React.ReactNode }
> = ({ title, description, className, children, ...props }) => (
    <section className={cn("min-w-0", className)} {...props}>
        {(title || description) && (
            <div className="mb-4 pb-3 border-b border-line-subtle">
                {title && <h3 className="text-title-sm font-semibold text-content">{title}</h3>}
                {description && (
                    <p className="text-meta text-content-muted mt-0.5 leading-relaxed">{description}</p>
                )}
            </div>
        )}
        {children}
    </section>
);

/** Sticky footer for long forms — actions never scroll out of reach. */
export const FormActions: React.FC<React.HTMLAttributes<HTMLDivElement> & { sticky?: boolean }> = ({
    sticky,
    className,
    children,
    ...props
}) => (
    <div
        className={cn(
            "flex flex-col-reverse sm:flex-row sm:justify-end items-stretch sm:items-center gap-2 pt-4 mt-2 border-t border-line-subtle",
            sticky && "sticky bottom-0 bg-surface -mx-4 sm:-mx-5 px-4 sm:px-5 pb-4 z-10",
            className
        )}
        {...props}
    >
        {children}
    </div>
);

export default Field;
